// @ts-strict-ignore
import clsx from 'clsx'
import { MutableRefObject, ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { Portal } from '@/shared/lib/components/Portal/Portal'
import { lockBodyScroll } from '@/shared/lib/dom/lock-body-scroll'

import cl from './full-screen-modal.module.scss'

const ANIMATION_DELAY = 200

type FullScreenModalProps = {
	children?: ReactNode
	open?: boolean
	onClose?: () => void
	coloredBg?: boolean
	className?: string
}

export const FullScreenModal = (props: FullScreenModalProps) => {
	const { children, open = false, onClose, coloredBg, className } = props

	const timerRef = useRef() as MutableRefObject<ReturnType<typeof setTimeout>>
	const [isClosing, setIsClosing] = useState(false)

	const close = useCallback(() => {
		setIsClosing(true)

		timerRef.current = setTimeout(() => {
			onClose?.()
			setIsClosing(false)
		}, ANIMATION_DELAY)
	}, [onClose])

	const closeRef = useRef(close)
	closeRef.current = close

	useEffect(() => {
		if (!open) return

		const unlockBodyScroll = lockBodyScroll()
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') closeRef.current()
		}

		window.addEventListener('keydown', onKeyDown)

		return () => {
			window.removeEventListener('keydown', onKeyDown)
			unlockBodyScroll()
		}
	}, [open])

	useEffect(() => {
		return () => {
			clearTimeout(timerRef.current)
		}
	}, [])

	const rootClasses = clsx(cl.root, className, {
		[cl.root__isClosing]: isClosing,
		[cl.coloredBg]: coloredBg
	})

	if (!open) return null

	return (
		<Portal element={document.querySelector('.app') ?? document.body}>
			<div className={rootClasses}>
				<button className={cl.close} onClick={close}>
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
						<g clipPath="url(#clip0_21038_35411)">
							<path
								d="M18.295 7.115C18.6844 6.72564 18.6844 6.09436 18.295 5.705C17.9056 5.31564 17.2744 5.31564 16.885 5.705L12 10.59L7.115 5.705C6.72564 5.31564 6.09436 5.31564 5.705 5.705C5.31564 6.09436 5.31564 6.72564 5.705 7.115L10.59 12L5.705 16.885C5.31564 17.2744 5.31564 17.9056 5.705 18.295C6.09436 18.6844 6.72564 18.6844 7.115 18.295L12 13.41L16.885 18.295C17.2744 18.6844 17.9056 18.6844 18.295 18.295C18.6844 17.9056 18.6844 17.2744 18.295 16.885L13.41 12L18.295 7.115Z"
								fill="#545563"
							/>
						</g>
						<defs>
							<clipPath id="clip0_21038_35411">
								<rect width="24" height="24" fill="white" />
							</clipPath>
						</defs>
					</svg>
				</button>
				{children}
			</div>
		</Portal>
	)
}
